import { mdiChevronLeft } from '@mdi/js';
import Icon from '@mdi/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Col } from 'reactstrap';
import moment from 'moment';
import { toast } from 'react-toastify';
import { fetchTravelOptions } from '../../../store/apis/travelService';
import { approvalsActions } from './constants';
import './TravelRequestApproval.scss';
import { FinalizedQuote } from '../../../components/Approvals';
import { approveRejectRequest } from '../../../store/apis/approvalService';
import RejectRequestModal from '../../../components/Approvals/RejectRequestModal';

function TravelRequestApproval() {
  document.title = 'Travel Request Approval | Envision - Daikin';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const id = searchParams.get('id');
  const revisionNo = searchParams.get('t');

  const { state: approvalDetails } = useLocation();

  const [rejectModal, setRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  const toggleRejectModal = () => setRejectModal(!rejectModal);

  // Fetch travel request approval details
  const { data, isLoading } = useQuery({
    queryKey: ['travelRequestApproval', id],
    queryFn: () =>
      fetchTravelOptions({
        requestNo: id,
        revisionNo: revisionNo || approvalDetails?.Typ,
      }),
    enabled: !!id && !!(revisionNo || approvalDetails?.Typ),
  });

  const { headerDetails, flightOptions, hotelOptions } = data || {};

  // Get selected flight and hotel options from header details
  const getSelectedOptionsFromHeader = () => {
    const selectedFlightNo = headerDetails?.Selected_Option_Flight;
    const selectedHotelNo = headerDetails?.Selected_Option_Hotel;

    const getFlightOption = num => {
      if (!num || num < 1 || num > 3) return null;

      return {
        travelAgency: headerDetails[`Option${num}_Flight_TravelAgency`],
        currency: headerDetails[`Option${num}_Flight_Curr`],
        travelAgencyDescription:
          headerDetails[`Option${num}_Flight_TravelAgency_Description`],
        quoteValue: headerDetails[`Option${num}_Flight_QuoteValue`],
        refund: headerDetails[`Option${num}_Flight_Refund`],
        rebooking: headerDetails[`Option${num}_Flight_Rebooking`],
        terms: headerDetails[`Option${num}_Flight_Terms`],
      };
    };

    const getHotelOption = num => {
      if (!num || num < 1 || num > 3) return null;

      return {
        currency: headerDetails[`Option${num}_Hotel_Curr`],
        ratesPerNight: headerDetails[`Option${num}_Hotel_RatesPerNight`],
        quoteValue: headerDetails[`Option${num}_Hotel_QuoteValue`],
        terms: headerDetails[`Option${num}_Hotel_Terms`],
      };
    };

    return {
      selectedFlightOption: getFlightOption(selectedFlightNo),
      selectedHotelOption: getHotelOption(selectedHotelNo),
    };
  };

  // Get selected flight and hotel options from header details
  const { selectedFlightOption, selectedHotelOption } =
    getSelectedOptionsFromHeader(headerDetails);

  // Render requirements badges based on header details
  const renderRequirements = () => {
    const requirements = ['Flight', 'Hotel', 'Visa', 'Travel Insurance'];
    const selected = requirements.filter(req => headerDetails[req] === 'Y');
    return selected.length > 0 ? selected.join(', ') : '-';
  };

  // Map the approval request to the format required by the API
  const mapApproveRejectRequest = (action, approval) => ({
    actionType: action,
    requestId: approval?.RequestID,
    requestedBy: approval?.RequestedBy,
    category: approval?.Category,
    typ: approval?.Typ,
    approvalRank: approval?.ApprovalRank,
    requestedDate: approval?.RequestDate,
    requestType: approval?.RequestType,
    details: approval?.Details,
    requestorId: approval?.Requester,
    coCd: approval?.CoCd,
    reason: approval?.Comment,
  });

  // Mutation for approving or rejecting the travel request
  const approveRejectTravelRequest = useMutation({
    mutationFn: approveRejectRequest,
    onSuccess: (_data, variables) => {
      if (variables?.actionType === 'R') {
        setRejectModal(false);
        setRejectReason('');
        toast.success('Travel request rejected successfully.', {
          className: 'toast-success',
        });
        navigate('/dashboard');
      } else if (variables?.actionType === 'A') {
        toast.success('Travel request approved successfully.', {
          className: 'toast-success',
        });
        navigate('/dashboard');
      }
    },
    onError: error => {
      const errorMessage =
        error?.err?.meta?.message ||
        error?.response?.data?.message ||
        error?.message ||
        'An error occurred';
      toast.error(errorMessage, {
        className: 'toast-error',
      });
    },
  });

  // Handle approve and rejection of approval
  const handleApproveRejectApproval = async (action, approval) => {
    const requestPayload = mapApproveRejectRequest(action, approval);
    if (action === approvalsActions.Reject) {
      if (!rejectReason) {
        toast.error('Please provide a reason for rejection', {
          className: 'toast-error',
        });
        return;
      }
      requestPayload.reason = rejectReason;
    }
    approveRejectTravelRequest.mutate(requestPayload);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="page-content bg-white mb-3">
      {/* Header */}
      <div
        style={{ borderBottom: '1px solid lightgrey' }}
        className="d-flex flex-row align-items-center justify-content-between gap-2 pb-2 mb-3"
      >
        <div className="d-flex flex-row align-items-center gap-2">
          <button
            type="button"
            className="btn btn-link p-0 back-btn fs-5"
            onClick={() => window.history.back()}
          >
            <Icon path={mdiChevronLeft} size={1.1} />
            <span className="d-flex flex-row gap-2 fs-5">Back</span>
          </button>

          <div
            style={{
              width: '1px',
              borderLeft: '1px solid lightgrey',
              height: '20px',
            }}
          />
          <div className="d-flex flex-row gap-1">
            <span className="request-id-span fs-5">Request No</span>
            <span className="request-id-span-value fs-5">{id}</span>
          </div>
          <div
            style={{
              width: '1px',
              borderLeft: '1px solid lightgrey',
              height: '20px',
            }}
          />
          <div>
            <span className="fw-semibold fs-5">Travel Request Approval</span>
          </div>
        </div>

        {/* Buttons */}
        <div className="d-flex flex-row gap-2">
          <button
            onClick={() =>
              handleApproveRejectApproval(
                approvalsActions.Approve,
                approvalDetails,
              )
            }
            type="button"
            className="custom-success-btn"
          >
            Approve
          </button>
          <button
            onClick={toggleRejectModal}
            type="button"
            className="custom-danger-btn"
          >
            Reject
          </button>
          <button
            onClick={() =>
              navigate(`/travel-request/options/${id}/${revisionNo}`)
            }
            type="button"
            className="custom-btn"
          >
            View Quote Options
          </button>
        </div>
      </div>
      {/* Body */}
      <div className="d-flex flex-column gap-2">
        {/* General Information */}
        <div className="d-flex flex-column gap-2">
          <div>
            <span className="fw-semibold fs-5">General Information</span>
          </div>
          <div className="external-filter gap-2 p-3">
            <Col lg={12} className="w-100">
              <div className="d-flex flex-column align-items-start w-100 gap-3">
                <div className="d-flex flex-row align-items-start w-100 justify-content-between">
                  <Col className="external-filter-col d-flex flex-column gap-3">
                    <Col className="d-flex flex-column">
                      <span className="ba-general-info-label">
                        Request Date
                      </span>
                      <span>
                        {headerDetails?.ReqDate?.trim()
                          ? moment(headerDetails?.ReqDate).format('LL')
                          : '-'}
                      </span>
                    </Col>
                    <Col className="d-flex flex-column">
                      <span className="ba-general-info-label">
                        Requirements
                      </span>
                      <span>{renderRequirements()}</span>
                    </Col>
                  </Col>
                  <Col className="external-filter-col gap-3">
                    <Col className="d-flex flex-column">
                      <span className="ba-general-info-label">
                        Type of Traveller
                      </span>
                      <span>
                        {headerDetails?.TravelerType_Description?.trim()
                          ? headerDetails?.TravelerType_Description
                          : '-'}
                      </span>
                    </Col>
                    <Col className="d-flex flex-column">
                      <span className="ba-general-info-label">
                        Purpose of Travel
                      </span>
                      <span>
                        {headerDetails?.TravelPurpouse?.trim()
                          ? headerDetails?.TravelPurpouse
                          : '-'}
                      </span>
                    </Col>
                  </Col>
                  <Col className="external-filter-col">
                    <span className="ba-general-info-label">Travel Type</span>
                    <span>
                      {headerDetails?.TravelType_Description?.trim()
                        ? headerDetails?.TravelType_Description
                        : '-'}
                    </span>
                  </Col>
                  <Col className="external-filter-col">
                    <span className="ba-general-info-label">Employee Name</span>
                    <span>
                      {headerDetails?.UserFullName?.trim()
                        ? headerDetails?.UserFullName
                        : '-'}
                    </span>
                  </Col>
                  <Col className="external-filter-col">
                    <span className="ba-general-info-label">SOR No</span>
                    <span>
                      {headerDetails?.SORNo?.trim()
                        ? headerDetails?.SORNo
                        : '-'}
                    </span>
                  </Col>
                </div>
              </div>
            </Col>
          </div>
        </div>

        <FinalizedQuote
          selectedFlightOption={selectedFlightOption}
          selectedHotelOption={selectedHotelOption}
          flightOptions={flightOptions}
          hotelOptions={hotelOptions}
          headerDetails={headerDetails}
          data={data}
        />
      </div>
      <RejectRequestModal
        isOpen={rejectModal}
        toggle={toggleRejectModal}
        onChange={setRejectReason}
        value={rejectReason}
        handleRejection={() =>
          handleApproveRejectApproval(approvalsActions.Reject, approvalDetails)
        }
      />
    </div>
  );
}

export default TravelRequestApproval;
